import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
// API
import { SEARCH_STORES } from 'api/store/queries';
import {
  SearchStores,
  SearchStoresVariables,
} from 'api/store/types/SearchStores';
import {
  SortDirection,
  StoreStatus,
  StoresOrderBy,
  UserRole,
} from 'api/graphql-global-types';
// Ui3
import TextField from 'ui3/TextField/TextField';
// Types
import { FightCompetitorDraft } from './fightCardTypes';
// Styles
import styles from './CompetitorSearchField.module.scss';

export type CompetitorSearchFieldProps = {
  name: string;
  value: string;
  disabled?: boolean;
  onTextChange: (text: string) => void;
  onSelectStore: (competitor: FightCompetitorDraft) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 8;

const CompetitorSearchField = ({
  name,
  value,
  disabled,
  onTextChange,
  onSelectStore,
  onFocus,
  onBlur,
}: CompetitorSearchFieldProps) => {
  const [focused, setFocused] = useState(false);
  const [term, setTerm] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => setTerm(value.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [value]);

  const skip = !focused || term.length < SEARCH_MIN_CHARS;
  const { data, loading } = useQuery<SearchStores, SearchStoresVariables>(
    SEARCH_STORES,
    {
      variables: {
        storeRoles: [
          UserRole.Athlete,
          UserRole.Organization,
          UserRole.ContentCreator,
        ],
        input: {
          storeName: term,
          status: [StoreStatus.Active],
          orderBy: StoresOrderBy.socialFollowerCount,
          direction: SortDirection.DESC,
          limit: SEARCH_LIMIT,
          offset: 0,
        },
      },
      skip,
      fetchPolicy: 'network-only',
    }
  );

  const stores = data?.stores?.entities ?? [];
  const showDropdown = !skip && (loading || stores.length > 0);

  return (
    <div className={styles.root}>
      <TextField
        name={name}
        fieldSize="small"
        prefixIcon="search"
        placeholder="Search on MILLIONS"
        autoComplete="off"
        disabled={disabled}
        value={value}
        onFocus={() => {
          setFocused(true);
          onFocus?.();
        }}
        onBlur={() => {
          setFocused(false);
          onBlur?.();
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onTextChange(event.target.value)
        }
      />

      {showDropdown && (
        <ul className={styles.dropdown}>
          {loading && stores.length === 0 ? (
            <li className={styles.dropdownEmpty}>Searching…</li>
          ) : (
            stores.map((store) => {
              const storeName = store.storeDetails?.storeName || '';
              const avatarURL = store.storeDetails?.avatarURL || null;
              return (
                <li key={store.id}>
                  <button
                    type="button"
                    className={styles.option}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() =>
                      onSelectStore({
                        storeId: store.id,
                        name: storeName,
                        avatarURL,
                        invitedEmail: null,
                        inviteToken: null,
                      })
                    }
                  >
                    {avatarURL ? (
                      <img
                        className={styles.optionAvatar}
                        src={avatarURL}
                        alt=""
                      />
                    ) : (
                      <span className={styles.optionAvatarFallback}>
                        {storeName.charAt(0).toUpperCase() || '?'}
                      </span>
                    )}
                    <span className={styles.optionName}>{storeName}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default CompetitorSearchField;
